import { motion } from 'framer-motion';

/**
 * Animated fake mouse cursor that moves through a series of targets.
 * targets: [{ x, y, click?, delay? }]  — positions are relative to the
 * nearest `position: relative` ancestor (usually the PortalShell content).
 */
export default function AutoCursor({ targets = [], startDelay = 0.2 }) {
  if (!targets.length) return null;

  const keyframes = {
    x: targets.map(t => t.x),
    y: targets.map(t => t.y),
  };
  const times = (() => {
    const total = targets.reduce((acc, t) => acc + (t.delay || 0.8), 0);
    let cum = 0;
    return targets.map(t => {
      cum += (t.delay || 0.8);
      return cum / total;
    });
  })();
  const totalDuration = targets.reduce((acc, t) => acc + (t.delay || 0.8), 0);

  return (
    <>
      <motion.div
        data-testid="auto-cursor"
        initial={{ x: targets[0].x, y: targets[0].y, opacity: 0, scale: 0.6 }}
        animate={{
          x: keyframes.x,
          y: keyframes.y,
          opacity: [0, 1, ...targets.slice(1).map(() => 1)],
          scale: 1,
        }}
        transition={{
          duration: totalDuration,
          times: [0, ...times.slice(0, -1)].map((t, i) => i === 0 ? 0 : t),
          ease: 'easeInOut',
          delay: startDelay,
        }}
        className="pointer-events-none absolute z-[80]"
        style={{ top: 0, left: 0 }}
      >
        {/* Cursor arrow SVG */}
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))' }}>
          <path d="M5 3 L5 19 L9 15 L12 22 L14 21 L11 14 L17 14 Z" fill="#0A0A0A" stroke="#ffffff" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      </motion.div>

      {/* Click ripples at target positions */}
      {targets.map((t, i) => {
        if (!t.click) return null;
        const rippleDelay = startDelay + targets.slice(0, i + 1).reduce((acc, x) => acc + (x.delay || 0.8), 0) - 0.15;
        return (
          <motion.div
            key={`ripple-${i}`}
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: [0, 0.8, 0], scale: [0.4, 1.6, 2.2] }}
            transition={{ duration: 0.6, delay: rippleDelay, ease: 'easeOut' }}
            className="pointer-events-none absolute z-[79] rounded-full border-2 border-teal-500"
            style={{ top: t.y - 14, left: t.x - 14, width: 28, height: 28 }}
          />
        );
      })}
    </>
  );
}
